import {
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    Link,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { CREATE, DataAPIContext, DELETE } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import DeleteButton from '../components/DeleteButton';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { ContextStore } from '../store/contextStore';
import { userCanAccessAirbnbUi } from './airbnbAccess';
import AirbnbThreadsDialog from './AirbnbThreadsDialog';

const useStyles = makeStyles((theme) => ({
    root: { padding: theme.spacing(2) },
    card: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
    // RecordList's DataGrid is height:100% and needs a sized parent, or its rows viewport
    // collapses to 0 (toolbar + footer show, but no rows).
    listWrapper: { height: `calc(${theme.cp.contentHeight} - 28.5rem)`, minHeight: '20rem' },
}));

const RESOURCE = 'airbnb_account';

const AirbnbAccountsPage = () => {
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const { tourOperator: currentTO } = React.useContext(ContextStore);
    const location = useLocation();
    const history = useHistory();

    // The operator is the dashboard you're signed into — not a choice. To onboard a different
    // operator, switch to that operator's dashboard.
    const tourOperator = currentTO ? { id: currentTO.id, name: currentTO.name } : null;
    const [email, setEmail] = React.useState('');
    const [code, setCode] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [lastUpdate, setLastUpdate] = React.useState();

    const [linkDialog, setLinkDialog] = React.useState({ open: false, url: '' });
    const [threadsFor, setThreadsFor] = React.useState(null);
    const [deleteTarget, setDeleteTarget] = React.useState(null);

    // Surface the result of the OAuth callback redirect (?airbnb_connect=success|error).
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const result = params.get('airbnb_connect');
        if (!result) return;
        if (result === 'success') {
            enqueueSnackbar(`Airbnb account connected (${params.get('email') || ''})`, {
                variant: 'success',
            });
            setLastUpdate(new Date().toString());
        } else {
            enqueueSnackbar(`Airbnb connect failed: ${params.get('message') || 'unknown error'}`, {
                variant: 'error',
            });
        }
        // Clear the query so a refresh doesn't re-toast.
        history.replace(location.pathname);
    }, [location.search, location.pathname, history, enqueueSnackbar]);

    if (!userCanAccessAirbnbUi()) {
        return <AccessDeniedView />;
    }

    const connectReady = tourOperator?.id && email.trim();

    const handleGenerateLink = async () => {
        if (!connectReady) return;
        setBusy(true);
        try {
            const res = await dataAPI(CREATE, RESOURCE, {
                data: { tour_operator: tourOperator.id, email: email.trim() },
                urlSuffix: 'oauth_url/',
            });
            if (res.statusCode === 200 && res.data?.url) {
                setLinkDialog({ open: true, url: res.data.url });
            } else {
                enqueueSnackbar(res.data?.message || 'Failed to generate link', { variant: 'error' });
            }
        } catch (e) {
            enqueueSnackbar('Failed to generate link', { variant: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const handleExchange = async () => {
        if (!connectReady || !code.trim()) return;
        setBusy(true);
        try {
            const res = await dataAPI(CREATE, RESOURCE, {
                data: { tour_operator: tourOperator.id, email: email.trim(), code: code.trim() },
                urlSuffix: 'exchange/',
            });
            if (res.statusCode === 201 || res.statusCode === 200) {
                enqueueSnackbar('Account connected', { variant: 'success' });
                setCode('');
                setLastUpdate(new Date().toString());
            } else {
                enqueueSnackbar(res.data?.message || 'Failed to connect account', { variant: 'error' });
            }
        } catch (e) {
            enqueueSnackbar('Failed to connect account', { variant: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const handleReauth = async (row) => {
        try {
            const res = await dataAPI(CREATE, RESOURCE, {
                data: {},
                urlSuffix: `${row.id}/reauth/`,
            });
            if (res.statusCode === 200 && res.data?.url) {
                setLinkDialog({ open: true, url: res.data.url });
            } else {
                enqueueSnackbar(res.data?.message || 'Failed to build re-auth link', { variant: 'error' });
            }
        } catch (e) {
            enqueueSnackbar('Failed to build re-auth link', { variant: 'error' });
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await dataAPI(DELETE, RESOURCE, { id: deleteTarget.id, data: {} });
            enqueueSnackbar('Account deleted', { variant: 'success' });
            setLastUpdate(new Date().toString());
        } catch (e) {
            enqueueSnackbar('Failed to delete account', { variant: 'error' });
        } finally {
            setDeleteTarget(null);
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(linkDialog.url);
            enqueueSnackbar('Link copied', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar('Copy failed — select and copy manually', { variant: 'warning' });
        }
    };

    const columns = [
        { field: 'airbnb_email', headerName: 'Airbnb email', flex: 1, minWidth: 200 },
        { field: 'airbnb_user_id', headerName: 'Airbnb user ID', minWidth: 140 },
        {
            field: 'token_is_stale',
            headerName: 'Token',
            minWidth: 120,
            renderCell: (p) =>
                p.row.has_auth_token && !p.row.token_is_stale ? (
                    <Chip size="small" color="success" label="Fresh" />
                ) : (
                    <Chip size="small" color="warning" label={p.row.has_auth_token ? 'Stale' : 'Missing'} />
                ),
        },
        { field: 'thread_count', headerName: 'Threads', width: 90 },
        {
            field: 'actions',
            headerName: 'Actions',
            sortable: false,
            filterable: false,
            minWidth: 300,
            renderCell: (p) => (
                <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => handleReauth(p.row)}>
                        Re-auth
                    </Button>
                    <Button size="small" onClick={() => setThreadsFor(p.row)}>
                        Threads
                    </Button>
                    <DeleteButton size="small" onClick={() => setDeleteTarget(p.row)}>
                        Delete
                    </DeleteButton>
                </Stack>
            ),
        },
    ];

    return (
        <Grid container className={classes.root} direction="column">
            <PageMeta title="Airbnb" />
            <PageHeading sx={{ mb: 2 }}>Airbnb</PageHeading>

            <Card className={classes.card}>
                <Typography variant="h6" gutterBottom>
                    Connect an Airbnb account
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Enter the Airbnb account email, then either send the operator a one-click
                    authorize link, or paste the <code>code</code> they get back after authorizing.
                </Typography>
                <Stack spacing={2} sx={{ maxWidth: 640 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Tour operator
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {tourOperator?.name || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            The dashboard you’re signed into. Switch dashboards to onboard a different operator.
                        </Typography>
                    </Box>
                    <TextField
                        label="Airbnb account email"
                        fullWidth
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="host@operator.com"
                    />
                    <Box>
                        <Button
                            variant="contained"
                            onClick={handleGenerateLink}
                            disabled={!connectReady || busy}
                        >
                            Generate authorize link
                        </Button>
                    </Box>
                    <Divider>or paste a code</Divider>
                    <Stack direction="row" spacing={1}>
                        <TextField
                            label="Authorization code (or pasted redirect URL)"
                            fullWidth
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                        />
                        <Button
                            variant="outlined"
                            onClick={handleExchange}
                            disabled={!connectReady || !code.trim() || busy}
                        >
                            Connect
                        </Button>
                    </Stack>
                </Stack>
            </Card>

            <Card className={classes.card} style={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                    Connected accounts
                </Typography>
                <div className={classes.listWrapper}>
                    <RecordList
                        resource={RESOURCE}
                        columns={columns}
                        deps={lastUpdate}
                        sortModel={[{ field: 'airbnb_email', sort: 'asc' }]}
                    />
                </div>
            </Card>

            {/* Generated OAuth link */}
            <Dialog
                open={linkDialog.open}
                onClose={() => setLinkDialog({ open: false, url: '' })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Airbnb authorize link</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Send this link to the operator (or open it yourself while logged into the
                        Airbnb host account). After they authorize, Airbnb sends them to a page whose
                        URL contains <code>?code=…</code> — have them copy that code (or the whole
                        URL) back to you, then paste it into the “or paste a code” box above and hit
                        Connect.
                    </DialogContentText>
                    <TextField value={linkDialog.url} fullWidth multiline minRows={2} InputProps={{ readOnly: true }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={copyLink}>Copy</Button>
                    <Link href={linkDialog.url} target="_blank" rel="noopener" underline="none">
                        <Button variant="contained">Open</Button>
                    </Link>
                    <Button onClick={() => setLinkDialog({ open: false, url: '' })}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete Airbnb account?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Remove the connection for {deleteTarget?.airbnb_email}? Stored tokens and its
                        thread links will be deleted. This does not affect the Airbnb account itself.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <DeleteButton onClick={handleDelete}>
                        Delete
                    </DeleteButton>
                </DialogActions>
            </Dialog>

            <AirbnbThreadsDialog
                account={threadsFor}
                open={!!threadsFor}
                onClose={() => setThreadsFor(null)}
            />
        </Grid>
    );
};

export default AirbnbAccountsPage;
